import { getTRPCClient, isDomainError } from "@repo/api";
import { type OrderSetReadDto, orderSetStepMemberId } from "@repo/data-store";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { type BookingCheckInput, useCheckOrderSetBooking } from "@/features/scheduling";
import type { PresentedOrderSetDto } from "@/lib/order-set-presentation";

/**
 * Only scheduling facts cross this boundary for an order the server already
 * has — existing clinical content stays server-owned, and the domain refuses a
 * definition on one ("Existing orders accept scheduling changes only").
 *
 * An order outside `persisted` is new, and the opposite rule applies: it must
 * carry the revision it pins and its answers, or the domain refuses it for
 * being undefined. Newness is derived rather than flagged — an id the server
 * does not have is exactly what the domain itself tests.
 *
 * Staged patient exams travel separately on Submit; their required diagnostics
 * participate in this scheduling shape like any other new procedure.
 *
 * A held order the draft revoked stays in its step and is also named in
 * `revokedOrderIds`: the domain refuses a shape that omits a held order, since
 * dropping one would delete it. `revokedOnServer` keeps an old revocation from
 * being proposed again.
 */
export function schedulingShape(
  set: Pick<OrderSetReadDto, "envelopeMinutes" | "steps">,
  persisted?: ReadonlySet<string>,
  revokedOnServer: ReadonlySet<string> = new Set(),
): BookingCheckInput["proposal"] {
  const newlyRevoked = (item: { orderId: string; status?: string }) =>
    item.status === "revoked" && Boolean(persisted?.has(item.orderId)) && !revokedOnServer.has(item.orderId);
  const revokedOrderIds = set.steps.flatMap((step) => step.orders.filter(newlyRevoked).map((order) => order.orderId));
  return {
    ...(revokedOrderIds.length ? { revokedOrderIds } : {}),
    envelopeMinutes: set.envelopeMinutes,
    steps: set.steps.map((step) => ({
      id: orderSetStepMemberId(step.stepId),
      placement: step.placement,
      orders: step.orders.map((order) =>
        !persisted || persisted.has(order.orderId)
          ? { orderId: order.orderId }
          : {
              orderId: order.orderId,
              definition: { url: order.definition.url, version: order.definition.version },
              parameterValues: order.parameterValues,
            },
      ),
    })),
  };
}

/** Match OrderSet's envelope growth without compacting authored placement or gaps. */
function reconcileEnvelope(set: PresentedOrderSetDto): PresentedOrderSetDto {
  const minutes = Math.max(
    set.envelopeMinutes,
    ...set.steps.map((step) => step.placement.offsetMinutes + step.placement.durationMinutes),
  );
  return { ...set, authoredEnvelopeMinutes: minutes, envelopeMinutes: minutes, durationMinutes: minutes };
}

export function useBookedOrderSetProposal(orderSet: PresentedOrderSetDto, visitId?: string) {
  const [draft, setDraft] = useState<PresentedOrderSetDto | null>(null);
  const [accepted, setAccepted] = useState<{ source: string; value: PresentedOrderSetDto } | null>(null);
  const [revision, setRevision] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // What the server holds, so the draft can tell its own additions apart from it.
  const persisted = useMemo(
    () =>
      new Set([
        ...orderSet.steps.map((step) => step.stepId),
        ...orderSet.steps.flatMap((step) => step.orders.map((order) => order.orderId)),
      ]),
    [orderSet],
  );
  const revokedOnServer = useMemo(
    () =>
      new Set(
        orderSet.steps
          .flatMap((step) => step.orders)
          .filter((item) => item.status === "revoked")
          .map((item) => item.orderId),
      ),
    [orderSet],
  );
  // Revocations also invalidate the acknowledged scheduling shape.
  const source = JSON.stringify([schedulingShape(orderSet), [...revokedOnServer]]);
  // Keep the acknowledged shape visible until the subscription catches up.
  const base = accepted?.source === source ? accepted.value : reconcileEnvelope(orderSet);
  const displayed = draft ?? base;
  const input = useMemo(
    () => (visitId && draft ? { visitId, proposal: schedulingShape(draft, persisted, revokedOnServer) } : null),
    [visitId, draft, persisted, revokedOnServer],
  );
  const check = useCheckOrderSetBooking(input, revision);
  const discard = () => {
    if (!submittingRef.current) setDraft(null);
  };
  const change = (next: PresentedOrderSetDto) => {
    if (visitId && !submittingRef.current) setDraft(reconcileEnvelope(next));
  };
  const submit = async () => {
    if (!input || !draft || submittingRef.current || check.status !== "checked" || !check.check.bookable) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await getTRPCClient().booking.submitOrderSetBooking.mutate(input);
      if (!result.success) {
        setRevision((value) => value + 1);
        return;
      }
      setAccepted({ source, value: draft });
      setDraft(null);
      toast.success("Booking updated.");
    } catch (error) {
      if (!isDomainError(error)) toast.error("Couldn't submit the booking. Your proposal is still here.");
      setRevision((value) => value + 1);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  return {
    displayed,
    persisted,
    change,
    discard,
    submit,
    submitting,
    dirty: draft !== null,
    check,
    recheck: () => setRevision((value) => value + 1),
  };
}
